/**
 * The plain sentence for a failed just-in-time PO call (BLU-1268). The marker
 * parsing lives in the one shared parser (`@/lib/jit-po-errors`); this maps
 * each refusal to words in the `orders` namespace so an operator never sees a
 * marker or an error code.
 */
import type { TFunction } from 'i18next';
import type { JitPoErrorKey } from '@threadheads/shared';
import { parseJitPoError } from '@/lib/jit-po-errors';
import { messageOf, trpcErrorCode } from '@/lib/trpc-error-utils';

/** Any `SOME_CODE:{json}` marker: never shown to an operator as is. */
const ANY_MARKER_RE = /^[A-Z][A-Z0-9_]*:\s*\{/;

/** One sentence per refusal; the Record makes a new backend key a type error here. */
const ERROR_TEXT_KEY: Record<JitPoErrorKey, string> = {
  NOT_FOUND: 'jit.errors.notFound',
  NO_LINES: 'jit.errors.noLines',
  NOT_OPEN: 'jit.errors.notOpen',
  NO_SUPPLIER: 'jit.errors.noSupplier',
  EDITED: 'jit.errors.edited',
  OPEN_EXISTS: 'jit.errors.openExists',
  NOT_RECEIVABLE: 'jit.errors.notReceivable',
  STATUS_REFUSED: 'jit.errors.statusRefused',
  DELETE_REFUSED: 'jit.errors.deleteRefused',
  LINE_NOT_JUST_IN_TIME: 'jit.errors.lineNotJustInTime',
  DEV_LINE_REFUSED: 'jit.errors.devLineRefused',
  SHIPMENT_REFUSED: 'jit.errors.shipmentRefused',
  XERO_NOT_COMPLETE: 'jit.errors.xeroNotComplete',
  XERO_ACCOUNT_UNSET: 'jit.errors.xeroAccountUnset',
  XERO_DEPOSIT_REFUSED: 'jit.errors.xeroDepositRefused',
  BLANK_JUST_IN_TIME: 'jit.errors.blankJustInTime',
  BATCH_NOT_FOUND: 'jit.errors.batchNotFound',
};

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

/**
 * `skuLabel` names a SKU id the refusal carries (LINE_NOT_JUST_IN_TIME), so the
 * sentence says which blank. A refusal that is not ours falls back to the
 * server's own words when they are a sentence, and to nothing when they are a
 * marker: the toast title then carries the meaning on its own.
 */
export function jitPoErrorText(
  t: TFunction,
  error: unknown,
  skuLabel: (skuId: string) => string | null = () => null,
): string | undefined {
  const info = parseJitPoError(error);
  if (info) {
    if (info.key === 'LINE_NOT_JUST_IN_TIME') {
      const names = stringList(info.detail.skuIds)
        .map(skuLabel)
        .filter((name): name is string => Boolean(name));
      if (names.length > 0) return t('jit.errors.lineNotJustInTimeNamed', { skus: names.join(', ') });
    }
    return t(ERROR_TEXT_KEY[info.key]);
  }
  if (trpcErrorCode(error) === 'FORBIDDEN') return t('jit.errors.forbidden');
  const message = messageOf(error).trim();
  if (!message || ANY_MARKER_RE.test(message)) return undefined;
  return message;
}
